import { Goal, HabitFrequency } from '@/types/goal';
import { ETypeTag } from './ETypeTag';
import { HabitCheckbox } from './HabitCheckbox';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Plus, Trash2, Target, Calendar, Pencil, Check, X, Lock, CheckCircle2, Trophy } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { differenceInDays, isPast, isToday, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useDateFormat } from '@/contexts/DateFormatContext';
import { AddHabitForm } from './AddHabitForm';
import { GoalProgressBadge } from './GoalProgressBadge';
import { UpgradeModal } from './subscription/UpgradeModal';
import { useAuth } from '@/hooks/useAuth';
import { canCreateHabit } from '@/lib/tier-utils';
import { trackLimitHit } from '@/lib/analytics';
import { BETA_CONFIG } from '@/config/beta';
import { SuggestedMentorsSection } from './goals/SuggestedMentorsSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

interface GoalCardProps {
  goal: Goal;
  onToggleHabit: (habitId: string) => void;
  onAddHabit: (title: string, frequency?: HabitFrequency, scheduleDays?: number | null, linkedActionId?: string) => void;
  onDelete: () => void;
  onComplete?: () => void;
  onUpdateTitle?: (title: string) => void;
  onNavigateToPlan?: (milestoneIndex: number) => void;
}

export const GoalCard = ({ goal, onToggleHabit, onAddHabit, onDelete, onComplete, onUpdateTitle, onNavigateToPlan }: GoalCardProps) => {
  const { user } = useAuth();
  const { formatDate } = useDateFormat();
  const [showInput, setShowInput] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(goal.title);
  const [isAtHabitLimit, setIsAtHabitLimit] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const habitsListRef = useRef<HTMLDivElement>(null);

  // Check habit limit
  useEffect(() => {
    const checkLimit = async () => {
      if (!user) return;
      const result = await canCreateHabit(user.id);
      setIsAtHabitLimit(!BETA_CONFIG.allFeaturesUnlocked && !result.allowed);
    };
    checkLimit();
  }, [user, goal.habits.length]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const completedToday = goal.habits.filter((h) =>
    h.completedDates.includes(today)
  ).length;
  const progress = goal.habits.length > 0 ? (completedToday / goal.habits.length) * 100 : 0;

  const handleAddHabit = (title: string, frequency: HabitFrequency, scheduleDays: number | null, linkedActionId?: string) => {
    onAddHabit(title, frequency, scheduleDays, linkedActionId);
    setShowInput(false);
    // Re-check limit after adding
    if (user) {
      canCreateHabit(user.id).then(result => setIsAtHabitLimit(!result.allowed));
    }
    // Scroll habit list to bottom to show the newly added habit
    requestAnimationFrame(() => {
      if (habitsListRef.current) {
        habitsListRef.current.scrollTo({ top: habitsListRef.current.scrollHeight, behavior: 'smooth' });
      }
    });
  };

  const handleAddHabitClick = async () => {
    if (!user) return;
    // In beta mode all features are unlocked — open immediately without async check
    if (BETA_CONFIG.allFeaturesUnlocked) {
      setShowInput(true);
      return;
    }
    try {
      const result = await canCreateHabit(user.id);
      if (!result.allowed) {
        trackLimitHit('habit', { currentCount: result.currentCount, limit: result.limit });
        setUpgradeModalOpen(true);
        return;
      }
    } catch {
      // If limit check fails, allow habit creation as a safe fallback
    }
    setShowInput(true);
  };

  const getTargetDateInfo = () => {
    if (!goal.targetDate) return null;
    
    const targetDate = new Date(goal.targetDate);
    const daysUntil = differenceInDays(targetDate, new Date());
    
    if (isToday(targetDate)) {
      return { text: 'Due today!', color: 'text-orange-500' };
    } else if (isPast(targetDate)) {
      return { text: `${Math.abs(daysUntil)} days overdue`, color: 'text-destructive' };
    } else if (daysUntil <= 7) {
      return { text: `${daysUntil} days left`, color: 'text-orange-500' };
    } else {
      return { text: formatDate(targetDate), color: 'text-muted-foreground' };
    }
  };

  const targetDateInfo = getTargetDateInfo();

  // Status-based left border color for visual hierarchy
  const borderColor = (() => {
    if (goal.progress >= 75) return 'border-emerald-500 dark:border-emerald-400';
    if (goal.progress >= 25) return 'border-primary/50';
    const daysSinceCreated = differenceInDays(new Date(), new Date(goal.createdAt));
    if (daysSinceCreated > 7) return 'border-amber-500 dark:border-amber-400';
    return 'border-primary/50';
  })();

  const handleSaveTitle = () => {
    if (editedTitle.trim() && editedTitle !== goal.title && onUpdateTitle) {
      onUpdateTitle(editedTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleCancelEdit = () => {
    setEditedTitle(goal.title);
    setIsEditingTitle(false);
  };

  return (
    <div className={cn("card-elevated rounded-xl p-6 space-y-5 animate-fade-in border border-border/50 relative border-l-4", borderColor)}>
      {/* Progress Badge */}
      <GoalProgressBadge 
        progress={progress} 
        className="absolute top-4 right-4 z-10"
      />
      
      <div className="flex items-start justify-between gap-4 pr-24">
        <div className="space-y-2 flex-1">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Target className="w-5 h-5 text-primary" />
            </div>
            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-xl font-display font-bold h-auto py-1"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <Button variant="ghost" size="icon" onClick={handleSaveTitle} className="h-8 w-8 min-h-[44px] min-w-[44px]" aria-label="Save goal title">
                  <Check className="w-4 h-4 text-green-500" />
                </Button>
                <Button variant="ghost" size="icon" onClick={handleCancelEdit} className="h-8 w-8 min-h-[44px] min-w-[44px]" aria-label="Cancel editing">
                  <X className="w-4 h-4 text-muted-foreground" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h3 className="text-xl font-display font-bold truncate" title={goal.title}>{goal.title}</h3>
                {onUpdateTitle && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsEditingTitle(true)}
                    className="h-6 w-6 min-h-[44px] min-w-[44px] opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Edit goal title"
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <p className="text-muted-foreground text-sm">{goal.description}</p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {goal.eTypes.map((type) => (
              <ETypeTag key={type} type={type} />
            ))}
            {targetDateInfo && (
              <div className={`flex items-center gap-1.5 text-xs ${targetDateInfo.color}`}>
                <Calendar className="w-3.5 h-3.5" />
                <span>{targetDateInfo.text}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onComplete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-green-500 min-h-[44px] min-w-[44px]"
                  aria-label="Mark goal as complete"
                >
                  <CheckCircle2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    Complete This Goal?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Congratulations! Completed goals will be moved to your achievement history.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onComplete}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700"
                  >
                    Yes, I Did It!
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive min-h-[44px] min-w-[44px]"
                aria-label="Delete goal"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Goal</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{goal.title}"? This will also remove all associated habits and cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {goal.habits.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Today's progress</span>
            <span className="font-semibold">{completedToday}/{goal.habits.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      <div className="space-y-3 relative z-[1]">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Daily Habits {goal.habits.length > 5 && <span className="normal-case font-normal">({goal.habits.length})</span>}
          </h4>
          {!showInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddHabitClick}
              className={`min-h-[44px] ${isAtHabitLimit ? "text-muted-foreground" : "text-primary hover:text-primary"}`}
            >
              {isAtHabitLimit ? (
                <>
                  <Lock className="w-4 h-4 mr-1" />
                  Add Habit
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Habit
                </>
              )}
            </Button>
          )}
        </div>

        <div
          ref={habitsListRef}
          className={cn(
            "space-y-3",
            goal.habits.length > 5 && "max-h-[320px] overflow-y-auto pr-1"
          )}
        >
          {goal.habits.map((habit) => (
            <HabitCheckbox
              key={habit.id}
              title={habit.title}
              completed={habit.completedDates.includes(today)}
              streak={habit.streak}
              onToggle={() => onToggleHabit(habit.id)}
              linkedAction={habit.linkedAction}
              onNavigateToPlan={onNavigateToPlan}
            />
          ))}
        </div>

        {showInput && (
          <AddHabitForm
            onSubmit={handleAddHabit}
            onCancel={() => setShowInput(false)}
          />
        )}

        {goal.habits.length === 0 && !showInput && (
          <p className="text-muted-foreground text-sm text-center py-4 border border-dashed border-border rounded-lg">
            No habits yet. Add daily habits to build momentum!
          </p>
        )}
      </div>

      {/* Suggested Mentors */}
      {goal.eTypes.length > 0 && (
        <SuggestedMentorsSection goalTitle={goal.title} eTypes={goal.eTypes} />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        trigger="habit_limit"
      />
    </div>
  );
};
